/**
 * Границы ошибок на уровне вкладки.
 *
 * §3.5: ошибка рендера одной вкладки не должна переключать пользователя
 * в отчётность. Показываем сообщение и даём повторить, оставаясь на маршруте.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Имя области — попадает в лог, чтобы было видно, что именно упало. */
  area: string;
  /**
   * Смена значения сбрасывает ошибку — для границы маршрута это адрес.
   * Без сброса ошибка одного раздела оставалась на экране после перехода
   * в другой, пока страницу не перезагрузят.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Подавлять ошибку нельзя (§28.2). На фазе 13 сюда добавится отправка
    // на сервер вместе с correlationId.
    console.error(`[${this.props.area}] ошибка рендера`, error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <p className="text-base font-bold" style={{ color: "var(--color-fg)" }}>
          Не удалось отобразить раздел
        </p>
        <p className="max-w-md text-sm" style={{ color: "var(--color-muted)" }}>
          {this.state.error.message}
        </p>
        <button
          type="button"
          onClick={this.retry}
          className="rounded-[var(--radius-md)] px-4 py-2 text-sm font-semibold"
          style={{
            background: "var(--color-accent)",
            color: "var(--color-accent-fg)",
          }}
        >
          Повторить
        </button>
      </div>
    );
  }
}
